import {Children, CSSProperties, ReactNode, isValidElement} from 'react';
import {useTranslation} from 'react-i18next';
import * as css from './TicketCardCarousel.css';
import CarouselArrows from './CarouselArrows';
import CarouselDots from './CarouselDots';
import useSnapCarousel from './useSnapCarousel';
import useFitScale from './useFitScale';
import SwipeHand from '../../../assets/icons/SwipeHand';

interface TicketCardCarouselProps {
    children: ReactNode;
    overlay?: ReactNode;
    'data-cy'?: string;
}

function TicketCardCarousel({children, overlay, 'data-cy': dataCy = 'entrance-ticket-carousel'}: TicketCardCarouselProps) {
    const {t} = useTranslation('eTicket');
    const cards = Children.toArray(children);
    const {viewportRef, activeIndex, scrollToIndex} = useSnapCarousel(cards.length);
    const {areaRef, scale, topGap} = useFitScale();
    const hasMany = cards.length > 1;

    return (
        <css.carousel data-cy={dataCy}>
            <css.cardArea ref={areaRef} style={{'--card-scale': scale, '--card-top-gap': `${topGap}px`} as CSSProperties}>
                <css.viewport ref={viewportRef}>
                    {cards.map((card, index) => (
                        <css.slide
                            key={index}
                            className={index === activeIndex ? 'is-active' : ''}
                            data-carousel-card
                            data-carousel-index={index}
                            data-printable={isValidElement<{printable?: boolean}>(card) && card.props.printable ? '' : undefined}
                        >
                            <css.cardFrame>{card}</css.cardFrame>
                        </css.slide>
                    ))}
                </css.viewport>
                {hasMany && <CarouselArrows count={cards.length} activeIndex={activeIndex} onSelect={scrollToIndex} />}
                {overlay}
            </css.cardArea>
            {hasMany && <CarouselDots count={cards.length} activeIndex={activeIndex} onSelect={scrollToIndex} />}
            {hasMany && (
                <css.swipeHint className={activeIndex > 0 ? 'is-hidden' : ''} data-cy="entrance-ticket-swipe-hint">
                    <SwipeHand />
                    {t('swipeToScan')}
                </css.swipeHint>
            )}
        </css.carousel>
    );
}

export default TicketCardCarousel;
